import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/auth';
import { useTranslation } from 'react-i18next';
import { Search, Bell, ChevronDown, LogOut, Globe, Clock, Sun, Moon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { applyTheme, loadTheme, Theme } from '../../theme';

const NAV = [
  { to: '/', icon: 'ti-layout-dashboard', key: 'dashboard' },
  { to: '/books', icon: 'ti-books', key: 'catalog' },
  { to: '/checkouts', icon: 'ti-arrows-exchange', key: 'circulation' },
  { to: '/members', icon: 'ti-users', key: 'members' },
  { to: '/inventory', icon: 'ti-clipboard-list', key: 'inventory' },
];

const getSysNav = (role: string) => {
  const all = [
    { to: '/opac', icon: 'ti-world', key: 'opac', roles: ['admin','director','librarian'] },
    { to: '/reports', icon: 'ti-chart-bar', key: 'reports', roles: ['admin','director','librarian'] },
    { to: '/staff', icon: 'ti-users-group', key: 'staff', roles: ['admin','director'] },
    { to: '/financial', icon: 'ti-report-money', key: 'financial', roles: ['admin','director'] },
    { to: '/accounting', icon: 'ti-abacus', key: 'accounting', roles: ['admin','director'] },
    { to: '/accounting-accounts', icon: 'ti-list-numbers', key: 'accounting_accounts', roles: ['admin'] },
    { to: '/notifications', icon: 'ti-bell', key: 'notifications', roles: ['admin','director','librarian'] },
    { to: '/import', icon: 'ti-file-import', key: 'import', roles: ['admin','director'] },
    { to: '/acquisitions', icon: 'ti-shopping-cart', key: 'acquisitions', roles: ['admin','director'] },
    { to: '/serials', icon: 'ti-news', key: 'serials', roles: ['admin','director','librarian'] },
    { to: '/branches', icon: 'ti-building', key: 'branches', roles: ['admin'] },
    { to: '/marc', icon: 'ti-database-import', key: 'marc', roles: ['admin','director'] },
    { to: '/settings', icon: 'ti-settings', key: 'settings', roles: ['admin'] },
  ];
  return all.filter(item => item.roles.includes(role));
};

function LiveClock({ dk }: { dk: boolean }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const i = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(i);
  }, []);
  return (
    <div style={{display:'flex',alignItems:'center',gap:6,fontSize:12}}>
      <Clock size={13} style={{color:'var(--text-faint)',flexShrink:0}} />
      <span style={{fontFamily:'monospace',color:'var(--text-muted)',letterSpacing:'0.03em'}}>
        {now.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit',second:'2-digit'})}
      </span>
      <span style={{color:'var(--border)',margin:'0 2px'}}>·</span>
      <span style={{color:'var(--text-faint)',fontSize:11}}>
        {now.toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'})}
      </span>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [theme, setTheme] = useState<Theme>(loadTheme);
  const [search, setSearch] = useState('');
  const userRole = user?.role || 'librarian';
  const SYS = getSysNav(userRole);
  const isMobile = window.innerWidth < 768;
  const [mobileOpen, setMobileOpen] = useState(false);
  const dk = theme === 'dark';

  useEffect(() => { applyTheme(theme); }, [theme]);

  const toggleTheme = () => {
    const next: Theme = dk ? 'light' : 'dark';
    setTheme(next);
    applyTheme(next);
  };

  const toggleLang = () => {
    const next = i18n.language === 'ka' ? 'en' : 'ka';
    i18n.changeLanguage(next);
    localStorage.setItem('liber_lang', next);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (search.trim()) { navigate(`/books?q=${encodeURIComponent(search)}`); setSearch(''); }
  };

  const sideW = collapsed ? 60 : 224;

  // Sidebar colors — always dark navy regardless of theme
  const sideText    = 'rgba(255,255,255,0.45)';
  const sideActive  = '#ffffff';
  const sideActiveBg = dk ? 'rgba(79,142,247,0.18)' : 'rgba(255,255,255,0.15)';
  const sideBorder  = 'rgba(255,255,255,0.07)';

  const NavItem = ({ to, icon, label, end }: { to:string; icon:string; label:string; end?:boolean }) => (
    <NavLink to={to} end={end}
      style={({ isActive }) => ({
        display: 'flex', alignItems: 'center', gap: 10,
        padding: collapsed ? '10px 0' : '9px 12px',
        justifyContent: collapsed ? 'center' : 'flex-start',
        margin: '1px 8px', borderRadius: 8,
        fontSize: 13, textDecoration: 'none',
        color: isActive ? sideActive : sideText,
        background: isActive ? sideActiveBg : 'transparent',
        borderLeft: isActive ? `3px solid var(--accent)` : '3px solid transparent',
        transition: 'all 0.15s',
        whiteSpace: 'nowrap', overflow: 'hidden',
        boxSizing: 'border-box',
      })}>
      <i className={`ti ${icon}`} style={{fontSize:17,flexShrink:0}} />
      {!collapsed && <span style={{fontWeight:500}}>{label}</span>}
    </NavLink>
  );

  return (
    <div style={{display:'flex',height:'100vh',background:'var(--bg-base)',color:'var(--text-secondary)',fontFamily:'system-ui,sans-serif'}}>
      {isMobile && mobileOpen && (
        <div onClick={() => setMobileOpen(false)}
          style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:99,backdropFilter:'blur(2px)'}} />
      )}

      {/* ── SIDEBAR ── */}
      <aside style={{
        width: sideW,
        background: 'var(--sidebar-bg)',
        borderRight: `1px solid ${sideBorder}`,
        display: 'flex', flexDirection: 'column', flexShrink: 0,
        transition: 'width 0.2s ease',
        position: isMobile ? 'fixed' : 'relative',
        zIndex: isMobile ? 100 : 1,
        height: '100vh',
        transform: isMobile && !mobileOpen ? 'translateX(-100%)' : 'translateX(0)',
        boxShadow: 'var(--shadow-md)',
      }}>
        {/* Logo */}
        <div style={{
          padding: '0 12px',
          height: 56, display: 'flex', alignItems: 'center', gap: 10,
          borderBottom: `1px solid ${sideBorder}`,
          overflow: 'hidden',
        }}>
          <div style={{
            width: 34, height: 34, borderRadius: 10, flexShrink: 0,
            background: 'linear-gradient(135deg,#2563eb,#1d4ed8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(37,99,235,0.4)',
          }}>
            <i className="ti ti-books" style={{fontSize:17,color:'#fff'}} />
          </div>
          {!collapsed && (
            <div style={{overflow:'hidden',flex:1}}>
              <div style={{fontSize:14,fontWeight:700,color:'#ffffff',letterSpacing:'-0.2px'}}>Folios</div>
              <div style={{fontSize:10,color:'rgba(255,255,255,0.35)',marginTop:1}}>Library System</div>
            </div>
          )}
          <button onClick={() => setCollapsed(!collapsed)}
            style={{
              marginLeft: 'auto', background: 'none', border: 'none',
              color: 'rgba(255,255,255,0.3)', cursor: 'pointer',
              padding: 4, borderRadius: 6, flexShrink: 0,
              transition: 'color 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.color='rgba(255,255,255,0.7)')}
            onMouseLeave={e => (e.currentTarget.style.color='rgba(255,255,255,0.3)')}>
            <i className={`ti ${collapsed ? 'ti-menu-2' : 'ti-layout-sidebar-left-collapse'}`} style={{fontSize:15}} />
          </button>
        </div>

        {/* Nav */}
        <nav style={{flex:1,padding:'8px 0',overflowY:'auto'}}>
          {!collapsed && (
            <div style={{padding:'10px 16px 6px',fontSize:10,color:'rgba(255,255,255,0.3)',letterSpacing:'0.1em',textTransform:'uppercase',fontWeight:600}}>
              მთავარი
            </div>
          )}
          {NAV.map(({ to, icon, key }) => (
            <NavItem key={to} to={to} icon={icon} label={t(key)} end={to==='/'} />
          ))}

          {!collapsed && (
            <div style={{padding:'14px 16px 6px',fontSize:10,color:'rgba(255,255,255,0.3)',letterSpacing:'0.1em',textTransform:'uppercase',fontWeight:600}}>
              სისტემა
            </div>
          )}
          {collapsed && <div style={{height:8}} />}
          {SYS.map(({ to, icon, key }) => (
            <NavItem key={to} to={to} icon={icon} label={t(key)} />
          ))}
        </nav>

        {/* Bottom */}
        <div style={{borderTop:`1px solid ${sideBorder}`,padding:'8px 0'}}>
          <button onClick={toggleTheme}
            title={dk ? 'Light თემა' : 'Dark თემა'}
            style={{
              display:'flex',alignItems:'center',gap:8,
              padding: collapsed ? '8px 0' : '8px 16px',
              justifyContent: collapsed ? 'center' : 'flex-start',
              width:'100%',background:'none',border:'none',
              color:'rgba(255,255,255,0.4)',fontSize:12,cursor:'pointer',
              transition:'color 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.color='rgba(255,255,255,0.8)')}
            onMouseLeave={e => (e.currentTarget.style.color='rgba(255,255,255,0.4)')}>
            {dk ? <Sun size={15}/> : <Moon size={15}/>}
            {!collapsed && <span>{dk ? 'Light' : 'Dark'}</span>}
          </button>

          <button onClick={toggleLang}
            title={i18n.language === 'ka' ? 'English' : 'ქართული'}
            style={{
              display:'flex',alignItems:'center',gap:8,
              padding: collapsed ? '8px 0' : '8px 16px',
              justifyContent: collapsed ? 'center' : 'flex-start',
              width:'100%',background:'none',border:'none',
              color:'rgba(255,255,255,0.4)',fontSize:12,cursor:'pointer',
              transition:'color 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.color='rgba(255,255,255,0.8)')}
            onMouseLeave={e => (e.currentTarget.style.color='rgba(255,255,255,0.4)')}>
            <Globe size={15} />
            {!collapsed && <span>{i18n.language === 'ka' ? 'EN' : 'ქარ'}</span>}
          </button>

          {/* User */}
          <div
            onClick={() => { logout(); navigate('/login'); }}
            title="გასვლა"
            style={{
              display:'flex',alignItems:'center',gap:10,
              padding: collapsed ? '8px 0' : '8px 12px',
              justifyContent: collapsed ? 'center' : 'flex-start',
              margin:'4px 8px 4px', borderRadius:8,cursor:'pointer',
              background:'rgba(255,255,255,0.04)',
              border:'1px solid rgba(255,255,255,0.07)',
              transition:'background 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background='rgba(255,255,255,0.09)')}
            onMouseLeave={e => (e.currentTarget.style.background='rgba(255,255,255,0.04)')}>
            <div style={{
              width:28,height:28,borderRadius:8,flexShrink:0,
              background:'linear-gradient(135deg,#2563eb,#7c3aed)',
              display:'flex',alignItems:'center',justifyContent:'center',
              fontSize:12,fontWeight:700,color:'#fff',
            }}>
              {user?.full_name?.[0]?.toUpperCase() || 'A'}
            </div>
            {!collapsed && (
              <>
                <div style={{overflow:'hidden',flex:1}}>
                  <div style={{fontSize:12,fontWeight:500,color:'rgba(255,255,255,0.85)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                    {user?.full_name}
                  </div>
                  <div style={{fontSize:10,color:'rgba(255,255,255,0.35)'}}>{user?.username}</div>
                </div>
                <LogOut size={13} style={{color:'rgba(255,255,255,0.3)',flexShrink:0}} />
              </>
            )}
          </div>
        </div>
      </aside>

      {/* ── MAIN ── */}
      <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
        {/* Header */}
        <header style={{
          height: 52, flexShrink: 0,
          background: 'var(--header-bg)',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', padding: '0 20px', gap: 16,
          boxShadow: 'var(--shadow-sm)',
        }}>
          {isMobile && (
            <button onClick={() => setMobileOpen(!mobileOpen)}
              style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',padding:'4px 8px',marginRight:4}}>
              <i className="ti ti-menu-2" style={{fontSize:20}} />
            </button>
          )}

          <LiveClock dk={dk} />

          <form onSubmit={handleSearch} style={{flex:1,maxWidth:360,position:'relative',marginLeft:8}}>
            <Search size={13} style={{position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',color:'var(--text-faint)',pointerEvents:'none'}} />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder={t('search_books')}
              style={{
                width:'100%',boxSizing:'border-box',
                background:'var(--bg-input)',
                border:'1px solid var(--border)',
                borderRadius:8,padding:'7px 12px 7px 32px',
                fontSize:12,color:'var(--text-secondary)',outline:'none',
                transition:'border-color 0.15s',
              }}
              onFocus={e => (e.target.style.borderColor='var(--border-active)')}
              onBlur={e => (e.target.style.borderColor='var(--border)')}
            />
          </form>

          <div style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:10}}>
            <button
              style={{
                position:'relative',background:'none',border:'none',
                color:'var(--text-muted)',cursor:'pointer',
                width:34,height:34,borderRadius:8,
                display:'flex',alignItems:'center',justifyContent:'center',
                transition:'background 0.15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background='var(--bg-hover)')}
              onMouseLeave={e => (e.currentTarget.style.background='none')}>
              <Bell size={17} />
              <span style={{
                position:'absolute',top:5,right:5,width:7,height:7,
                background:'var(--danger)',borderRadius:'50%',
                border:`2px solid var(--header-bg)`,
              }} />
            </button>

            <Link to="/profile"
              style={{
                display:'flex',alignItems:'center',gap:8,padding:'5px 10px 5px 6px',
                borderRadius:8,textDecoration:'none',
                border:'1px solid var(--border)',background:'var(--bg-card-alt)',
                transition:'border-color 0.15s, background 0.15s',
              }}
              onMouseEnter={e => { (e.currentTarget as any).style.borderColor='var(--border-active)'; }}
              onMouseLeave={e => { (e.currentTarget as any).style.borderColor='var(--border)'; }}>
              <div style={{
                width:24,height:24,borderRadius:6,flexShrink:0,
                background:'linear-gradient(135deg,var(--accent),var(--purple,#7c3aed))',
                display:'flex',alignItems:'center',justifyContent:'center',
                fontSize:11,fontWeight:700,color:'#fff',
              }}>
                {user?.full_name?.[0]?.toUpperCase() || 'A'}
              </div>
              <span style={{fontSize:12,color:'var(--text-muted)',maxWidth:120,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                {user?.full_name}
              </span>
              <ChevronDown size={12} style={{color:'var(--text-faint)',flexShrink:0}} />
            </Link>
          </div>
        </header>

        <main style={{flex:1,overflowY:'auto',background:'var(--bg-base)'}}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
